/* src/views/logements/FicheChecklistCtrl.jsx — L'ONGLET « Checklist » de la
   fiche logement (lot 4, 2026-08-28) : la carte d'état (pièces · points ·
   photos), les pièces réordonnables aux flèches du proto, la feuille de
   pièce (nom, icône, photos exigées, points), « Reprendre d'un autre
   logement » (NEUF — remplace la checklist), « Ce que voit l'agent », et
   le renvoi « Les incidents » en FEUILLE PROVISOIRE (décision 3 : leur
   place sera Réglages · Incidents ; en attendant ils restent par logement
   et s'éditent ici — avec les poignées de réordonnancement BRANCHÉES,
   décision « fais-les marcher »).

   Données : log.checklists (draft cfg, persisté par la pastille
   Enregistrer) — mêmes chemins que ChecklistsTab (defaults 5 pièces,
   migration legacy array plat → pièces conservée). */

import { useState, useEffect, useMemo } from 'react'
import Feuille from '../accueil/Feuille'
import { ToggleRond } from './proto/LivretProto'

const ICONES = ['🛋️', '🍳', '🛁', '🛏️', '🚪', '🌳', '🚿', '🍽️', '🪑', '👕', '🧺', '📦', '🚗', '🏠']

const DEFAULT_PIECES = [
  { name: 'Salon', icon: '🛋️', photos_min: 1, items: ['Aspirer le sol', 'Dépoussiérer les surfaces', 'Plier coussins et plaids', 'Vider les poubelles', 'Aérer la pièce', 'Vérifier la télécommande'] },
  { name: 'Cuisine', icon: '🍳', photos_min: 2, items: ['Vider le lave-vaisselle', 'Nettoyer le plan de travail', "Nettoyer l'évier", 'Vérifier le frigo (vide, propre)', 'Nettoyer le micro-ondes', 'Vider la poubelle'] },
  { name: 'Salle de bain', icon: '🛁', photos_min: 2, items: ['Nettoyer la douche', 'Nettoyer le lavabo', 'Nettoyer les toilettes', 'Changer les serviettes', 'Vérifier le stock papier toilette', 'Nettoyer le miroir', 'Passer la serpillière'] },
  { name: 'Chambre', icon: '🛏️', photos_min: 1, items: ['Changer les draps', 'Aspirer le sol', 'Dépoussiérer', 'Vérifier sous le lit'] },
  { name: 'Entrée', icon: '🚪', photos_min: 0, items: ['Passer la serpillière', 'Vérifier les clés'] },
]

let seq = 0
const nid = (p) => `${p}${Date.now().toString(36)}_${(seq++).toString(36)}`

function normaliserPieces(menage) {
  // Migration défensive legacy (array plat d'items) → pièces, comme ChecklistsTab.
  if (Array.isArray(menage)) {
    return [{ id: nid('p_'), name: 'Checklist', icon: '🏠', order: 0, photos_min: 1,
      items: menage.filter(Boolean).map((it, i) => (typeof it === 'string'
        ? { id: nid('it_'), label: it, checked_default: true }
        : { id: it.id || nid('it_'), label: it.label || '', checked_default: it.checked_default !== false })) }]
  }
  return Array.isArray(menage?.pieces) ? menage.pieces.filter(Boolean) : []
}

function piecesParDefaut() {
  return DEFAULT_PIECES.map((p, i) => ({
    id: nid('p_'), name: p.name, icon: p.icon, order: i, photos_min: p.photos_min,
    items: p.items.map((label) => ({ id: nid('it_'), label, checked_default: true })),
  }))
}

const carteRow = { background: '#fff', borderRadius: '14px', marginBottom: '8px',
  overflow: 'hidden', boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }
const flecheStyle = (active) => ({ width: '26px', height: '20px', borderRadius: '5px',
  border: '1px solid #d5e3ee', background: '#fff', fontSize: '9px',
  color: active ? 'var(--navy)' : '#dde4ea', cursor: active ? 'pointer' : 'default' })
const inpFeuille = { width: '100%', minHeight: '38px', padding: '0 12px',
  borderRadius: '9px', background: '#f8fafc', border: '1px solid #d5e3ee',
  fontSize: '12.5px', color: 'var(--navy-d)', outline: 'none', boxSizing: 'border-box' }

export default function FicheChecklistCtrl({ draft }) {
  const { selected, c } = draft
  const log = selected.entity
  const pieces = useMemo(() => normaliserPieces(log.checklists?.menage), [log.checklists])
  const incidents = log.checklists?.incidents || {}

  const [feuille, setFeuille] = useState(null)
  // null | {piece: id} | {supprimerPiece: id} | 'reprendre' | 'vue-agent' | 'incidents'
  useEffect(() => { setFeuille(null) }, [log.id])

  const ecrirePieces = (nextPieces) => {
    draft.updateLog('checklists', {
      ...(log.checklists || {}),
      menage: { ...(typeof log.checklists?.menage === 'object' && !Array.isArray(log.checklists?.menage) ? log.checklists.menage : {}), pieces: nextPieces.map((p, i) => ({ ...p, order: i })) },
    })
  }
  const ecrireIncidents = (patch) => {
    draft.updateLog('checklists', {
      ...(log.checklists || {}),
      incidents: { ...incidents, ...patch },
    })
  }

  const bougerPiece = (idx, delta) => {
    const cible = idx + delta
    if (cible < 0 || cible >= pieces.length) return
    const next = [...pieces]
    const [p] = next.splice(idx, 1)
    next.splice(cible, 0, p)
    ecrirePieces(next)
  }

  const patchPiece = (id, patch) => {
    ecrirePieces(pieces.map((p) => (p.id === id ? { ...p, ...patch } : p)))
  }

  const nbPoints = pieces.reduce((s, p) => s + (p.items?.length || 0), 0)
  const nbPhotos = pieces.reduce((s, p) => s + (Number(p.photos_min) || 0), 0)
  const sansPhoto = pieces.filter((p) => !(Number(p.photos_min) > 0)).length

  // Le parc sans checklist (pour la carte ciel + la feuille Reprendre).
  const feuillesParc = useMemo(() => {
    const out = []
    for (const prop of c.props || []) {
      for (const l of prop.logs || []) {
        if (Array.isArray(l.chambres) && l.chambres.length > 0) {
          for (const ch of l.chambres) out.push(ch)
        } else out.push(l)
      }
    }
    return out
  }, [c])
  const aChecklist = (entity) => normaliserPieces(entity.checklists?.menage)
    .some((p) => (p.items?.length || 0) > 0)
  const sansChecklist = feuillesParc.filter((f) => f.id !== log.id && !aChecklist(f)).length

  const pieceOuverte = feuille?.piece ? pieces.find((p) => p.id === feuille.piece) : null

  return (
    <>
      {/* ── la carte d'état ───────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: '14px', padding: '11px 13px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap' }}>
          <span style={{ width: '34px', height: '34px', borderRadius: '10px',
            background: 'var(--vert-bg)', color: 'var(--vert-tx)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: '15px',
            flexShrink: '0' }}>✓</span>
          <span style={{ flex: '1', minWidth: '140px' }}>
            <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
              color: 'var(--navy-d)' }}>La checklist de ce logement</span>
            <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
              marginTop: '1px', lineHeight: '1.45' }}>c'est ce que l'agent coche
              sur son téléphone, pièce par pièce</span>
          </span>
          <span style={{ display: 'flex', gap: '14px', flexShrink: '0' }}>
            {[[pieces.length, 'pièces'], [nbPoints, 'points'], [nbPhotos, 'photos']].map(([n, lb]) => (
              <span key={lb} style={{ textAlign: 'right' }}>
                <b style={{ display: 'block', fontSize: '15px', color: 'var(--navy-d)',
                  lineHeight: '1' }}>{n}</b>
                <span style={{ display: 'block', fontSize: '9px', color: 'var(--gris-d)',
                  marginTop: '2px' }}>{lb}</span>
              </span>
            ))}
          </span>
        </div>
      </div>

      {sansPhoto > 0 && pieces.length > 0 && (
        <div className="warn">{sansPhoto} pièce{sansPhoto > 1 ? 's' : ''} n'exige{sansPhoto > 1 ? 'nt' : ''} aucune
          photo. Vous n'aurez aucune preuve de {sansPhoto > 1 ? 'leur' : 'son'} état si un voyageur conteste.</div>
      )}

      {/* ── les pièces ────────────────────────────────────────────── */}
      {pieces.length === 0 && (
        <div className="carte" style={{ textAlign: 'center', padding: '20px 14px' }}>
          <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: '0 0 10px',
            lineHeight: 1.55 }}>Aucune checklist pour ce logement — l'agent ne
            coche rien, aucune photo n'est exigée.</p>
          <button className="bt bt-s" data-testid="check-cinq-defaut"
            onClick={() => {
              if (!window.confirm('Charger les 5 pièces par défaut (Salon, Cuisine, Salle de bain, Chambre, Entrée) ?')) return
              ecrirePieces(piecesParDefaut())
            }}>Charger les 5 pièces par défaut</button>
        </div>
      )}
      {pieces.map((p, idx) => (
        <div key={p.id} style={carteRow}>
          <button onClick={() => setFeuille({ piece: p.id })}
            data-testid={`check-piece-${idx}`}
            style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
              textAlign: 'left', background: '#fff', border: 'none',
              padding: '11px 12px', minHeight: '58px', cursor: 'pointer' }}>
            <span style={{ width: '30px', height: '30px', borderRadius: '9px',
              flexShrink: '0', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '13px', background: '#eef2f6',
              color: '#5c6773' }}>{p.icon || '🏠'}</span>
            <span style={{ flex: '1', minWidth: '0' }}>
              <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
                color: 'var(--navy-d)' }}>{p.name || 'Sans nom'}</span>
              <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
                marginTop: '1px' }}>
                {(p.items?.length || 0)} point{(p.items?.length || 0) > 1 ? 's' : ''} · {Number(p.photos_min) > 0
                  ? `${p.photos_min} photo${p.photos_min > 1 ? 's' : ''}` : 'aucune photo'}</span>
            </span>
            {!(Number(p.photos_min) > 0) && (
              <span style={{ width: '7px', height: '7px', borderRadius: '50%',
                background: '#d98c1f', flexShrink: '0' }} />
            )}
            <span style={{ display: 'flex', flexDirection: 'column', gap: '1px',
              flexShrink: '0' }} onClick={(e) => e.stopPropagation()}>
              <button onClick={() => bougerPiece(idx, -1)} disabled={idx === 0}
                aria-label={`Monter ${p.name}`}
                style={flecheStyle(idx > 0)}>▲</button>
              <button onClick={() => bougerPiece(idx, 1)} disabled={idx === pieces.length - 1}
                aria-label={`Descendre ${p.name}`}
                style={flecheStyle(idx < pieces.length - 1)}>▼</button>
            </span>
            <span style={{ fontSize: '15px', color: 'var(--gris-d)',
              flexShrink: '0' }}>›</span>
          </button>
        </div>
      ))}

      <div className="act">
        <button className="bt bt-s" data-testid="check-nouvelle-piece"
          onClick={() => {
            const p = { id: nid('p_'), name: 'Nouvelle pièce', icon: '🏠',
              order: pieces.length, photos_min: 1, items: [] }
            ecrirePieces([...pieces, p])
            setFeuille({ piece: p.id })
          }}>＋ Une pièce</button>
        <button className="bt bt-p" data-testid="check-reprendre"
          onClick={() => setFeuille('reprendre')}>Reprendre d'un autre logement</button>
        {pieces.length > 0 && (
          <button className="bt bt-s" data-testid="check-reinitialiser"
            onClick={() => {
              if (!window.confirm('Réinitialiser aux 5 pièces par défaut ? Vos pièces personnalisées seront perdues.')) return
              ecrirePieces(piecesParDefaut())
            }}>Réinitialiser</button>
        )}
      </div>

      {sansChecklist > 0 && (
        <div className="carte" style={{ margin: '12px 0 0', background: 'var(--ciel-p)',
          borderColor: '#c3d5e4' }}>
          <span style={{ display: 'block', fontSize: '12px', fontWeight: '600',
            color: 'var(--navy-d)' }}>{sansChecklist} logement{sansChecklist > 1 ? 's' : ''} n'{sansChecklist > 1 ? 'ont' : 'a'} aucune checklist</span>
          <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
            marginTop: '1px', lineHeight: '1.45' }}>reprendre celle-ci prend
            quelques secondes, la ressaisir prend une heure</span>
        </div>
      )}

      {/* ── ce que voit l'agent ───────────────────────────────────── */}
      <p className="inter">Ce que voit l'agent</p>
      <button onClick={() => setFeuille('vue-agent')} data-testid="check-vue-agent"
        style={{ display: 'flex', alignItems: 'center', gap: '11px', width: '100%',
          textAlign: 'left', background: '#fff', border: 'none', borderRadius: '12px',
          padding: '11px 12px', minHeight: '58px', cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <span style={{ width: '30px', height: '30px', borderRadius: '9px',
          background: 'var(--ciel-p)', color: 'var(--navy)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '13px',
          flexShrink: '0' }}>◫</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>Voir sa journée</span>
          <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
            marginTop: '1px' }}>la checklist telle qu'elle apparaît sur son téléphone</span>
        </span>
        <span style={{ fontSize: '15px', color: 'var(--gris-d)', flexShrink: '0' }}>›</span>
      </button>

      {/* ── le renvoi incidents (feuille provisoire — décision 3) ─── */}
      <p className="inter">Ce qui se règle une fois pour tous</p>
      <button onClick={() => setFeuille('incidents')} data-testid="check-renvoi-incidents"
        style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
          textAlign: 'left', background: '#f8fafc', border: '1px solid var(--bord)',
          borderRadius: '12px', padding: '10px 12px', minHeight: '54px',
          cursor: 'pointer' }}>
        <span style={{ width: '26px', height: '26px', borderRadius: '8px',
          background: '#eef2f6', color: '#5c6773', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '12px',
          flexShrink: '0' }}>⚙</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '11.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>Les incidents</span>
          <span style={{ display: 'block', fontSize: '10px', color: 'var(--gris)',
            marginTop: '1px' }}>catégories, modèles et photos exigées — bientôt
            les mêmes pour tout le parc</span>
        </span>
        <span style={{ fontSize: '14px', color: 'var(--gris-d)', flexShrink: '0' }}>›</span>
      </button>
      <p className="note">La checklist de ménage est propre à chaque logement.
        Ce qui touche aux incidents ne l'est pas.</p>

      {/* ═══ les feuilles ══════════════════════════════════════════ */}
      {pieceOuverte && !feuille?.supprimerPiece && (
        <FeuillePiece piece={pieceOuverte}
          onPatch={(patch) => patchPiece(pieceOuverte.id, patch)}
          onSupprimer={() => setFeuille({ piece: pieceOuverte.id, supprimerPiece: pieceOuverte.id })}
          onFermer={() => setFeuille(null)} />
      )}
      {feuille?.supprimerPiece && (
        <Feuille titre={`Supprimer « ${pieces.find((p) => p.id === feuille.supprimerPiece)?.name || ''} »`}
          testId="feuille-supprimer-piece"
          onFermer={() => setFeuille({ piece: feuille.supprimerPiece })}
          enfants={(
            <>
              <div className="dang">Les points de cette pièce seront perdus.{' '}
                <b>Les ménages déjà faits ne changent pas</b> — seuls les
                prochains ne verront plus cette pièce.</div>
              <div className="act">
                <button className="bt bt-p" data-testid="supprimer-piece-confirmer"
                  onClick={() => {
                    ecrirePieces(pieces.filter((p) => p.id !== feuille.supprimerPiece))
                    setFeuille(null)
                  }}>Supprimer</button>
                <button className="bt bt-s"
                  onClick={() => setFeuille({ piece: feuille.supprimerPiece })}>Annuler</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'reprendre' && (
        <Feuille titre="Reprendre d'un autre logement" testId="feuille-reprendre-checklist"
          onFermer={() => setFeuille(null)}
          enfants={(
            <>
              <div className="info">La checklist du logement choisi{' '}
                <b>remplace celle-ci</b>. Vous pourrez la modifier ensuite.</div>
              {feuillesParc.filter((f) => f.id !== log.id).map((f) => {
                const src = normaliserPieces(f.checklists?.menage)
                const points = src.reduce((s, p) => s + (p.items?.length || 0), 0)
                const ok = points > 0
                return (
                  <button key={f.id} disabled={!ok}
                    data-testid={`reprendre-${f.id}`}
                    onClick={() => {
                      // Copie profonde avec ids NEUFS (jamais de collision).
                      const copie = src.map((p, i) => ({
                        id: nid('p_'), name: p.name, icon: p.icon, order: i,
                        photos_min: p.photos_min,
                        items: (p.items || []).map((it) => ({ id: nid('it_'),
                          label: it.label, checked_default: it.checked_default !== false })),
                      }))
                      ecrirePieces(copie)
                      setFeuille(null)
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px',
                      width: '100%', textAlign: 'left', background: '#fff',
                      border: '1px solid var(--bord)', borderRadius: '12px',
                      padding: '10px 12px', minHeight: '54px', marginBottom: '7px',
                      cursor: ok ? 'pointer' : 'default', opacity: ok ? 1 : 0.5 }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%',
                      flexShrink: '0', background: ok ? '#2f9e6e' : '#c3cdd6' }} />
                    <span style={{ flex: '1', minWidth: '0' }}>
                      <span style={{ display: 'block', fontSize: '12px',
                        fontWeight: '600', color: 'var(--navy-d)' }}>{f.nom_complet || f.nom || f.id}</span>
                      <span style={{ display: 'block', fontSize: '10px',
                        color: 'var(--gris)', marginTop: '1px' }}>
                        {ok ? `${src.length} pièce${src.length > 1 ? 's' : ''} · ${points} points` : 'aucune checklist'}</span>
                    </span>
                    {ok && <span style={{ fontSize: '14px', color: 'var(--gris-d)' }}>›</span>}
                  </button>
                )
              })}
              <div className="act">
                <button className="bt bt-s" onClick={() => setFeuille(null)}>Annuler</button>
              </div>
            </>
          )} />
      )}
      {feuille === 'vue-agent' && (
        <FeuilleVueAgent log={log} pieces={pieces} onFermer={() => setFeuille(null)} />
      )}
      {feuille === 'incidents' && (
        <FeuilleIncidentsProvisoire incidents={incidents}
          onEcrire={ecrireIncidents} onFermer={() => setFeuille(null)} />
      )}
    </>
  )
}

/* ── la feuille de pièce (nom · icône · photos · points) ──────────── */
function FeuillePiece({ piece, onPatch, onSupprimer, onFermer }) {
  const [pickerOuvert, setPickerOuvert] = useState(false)
  const items = piece.items || []

  const patchItem = (id, patch) => {
    onPatch({ items: items.map((it) => (it.id === id ? { ...it, ...patch } : it)) })
  }
  const bougerItem = (idx, delta) => {
    const cible = idx + delta
    if (cible < 0 || cible >= items.length) return
    const next = [...items]
    const [it] = next.splice(idx, 1)
    next.splice(cible, 0, it)
    onPatch({ items: next })
  }

  return (
    <Feuille titre={piece.name || 'La pièce'} testId="feuille-piece" onFermer={onFermer}
      enfants={(
        <>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center',
            marginBottom: '10px' }}>
            <button onClick={() => setPickerOuvert((v) => !v)}
              aria-label="Changer l'icône" data-testid="piece-icone"
              style={{ width: '38px', height: '38px', borderRadius: '9px',
                border: '1px solid #d5e3ee', background: '#f8fafc', fontSize: '17px',
                cursor: 'pointer', flexShrink: 0 }}>{piece.icon || '🏠'}</button>
            <input value={piece.name || ''} data-testid="piece-nom"
              onChange={(e) => onPatch({ name: e.target.value })}
              placeholder="Nom de la pièce" style={inpFeuille} />
          </div>
          {pickerOuvert && (
            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap',
              marginBottom: '10px' }}>
              {ICONES.map((ic) => (
                <button key={ic} onClick={() => { onPatch({ icon: ic }); setPickerOuvert(false) }}
                  style={{ width: '34px', height: '34px', borderRadius: '8px',
                    border: piece.icon === ic ? '2px solid var(--navy)' : '1px solid #d5e3ee',
                    background: '#fff', fontSize: '16px', cursor: 'pointer' }}>{ic}</button>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
            padding: '9px 0', borderTop: '1px solid var(--bord)',
            borderBottom: '1px solid var(--bord)', marginBottom: '9px' }}>
            <span style={{ flex: '1', fontSize: '12px', color: 'var(--gris)' }}>
              Photos exigées de l'agent</span>
            <button aria-label="Diminuer photos exigées" data-testid="piece-photos-moins"
              onClick={() => onPatch({ photos_min: Math.max(0, (Number(piece.photos_min) || 0) - 1) })}
              style={{ width: '32px', height: '32px', borderRadius: '8px',
                border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                color: 'var(--navy)', cursor: 'pointer' }}>−</button>
            <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '14px',
              fontWeight: '600' }}>{Number(piece.photos_min) || 0}</span>
            <button aria-label="Augmenter photos exigées" data-testid="piece-photos-plus"
              onClick={() => onPatch({ photos_min: Math.min(10, (Number(piece.photos_min) || 0) + 1) })}
              style={{ width: '32px', height: '32px', borderRadius: '8px',
                border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                color: 'var(--navy)', cursor: 'pointer' }}>+</button>
          </div>
          {!(Number(piece.photos_min) > 0) && (
            <div className="warn">Sans photo exigée, aucune preuve de l'état de
              cette pièce si un voyageur conteste.</div>
          )}
          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 6px' }}>
            Les points ({items.length})</p>
          {items.map((it, idx) => (
            <div key={it.id} style={{ display: 'flex', alignItems: 'center',
              gap: '7px', marginBottom: '6px' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '1px',
                flexShrink: 0 }}>
                <button onClick={() => bougerItem(idx, -1)} disabled={idx === 0}
                  aria-label="Monter le point"
                  style={flecheStyle(idx > 0)}>▲</button>
                <button onClick={() => bougerItem(idx, 1)} disabled={idx === items.length - 1}
                  aria-label="Descendre le point"
                  style={flecheStyle(idx < items.length - 1)}>▼</button>
              </span>
              <input type="checkbox" checked={it.checked_default !== false}
                title="Coché par défaut côté ménage"
                onChange={(e) => patchItem(it.id, { checked_default: e.target.checked })}
                style={{ flexShrink: 0 }} />
              <input value={it.label || ''}
                onChange={(e) => patchItem(it.id, { label: e.target.value })}
                placeholder="ex : Aspirer le sol"
                style={{ ...inpFeuille, minHeight: '34px', flex: 1 }} />
              <button onClick={() => onPatch({ items: items.filter((x) => x.id !== it.id) })}
                aria-label="Supprimer le point"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: '#b8412b', fontSize: '14px', flexShrink: 0 }}>✕</button>
            </div>
          ))}
          <button className="bt bt-s" data-testid="piece-ajouter-item"
            onClick={() => onPatch({ items: [...items, { id: nid('it_'), label: '', checked_default: true }] })}>
            ＋ Un point</button>
          <div style={{ marginTop: '10px', paddingTop: '10px',
            borderTop: '1px solid var(--bord)' }}>
            <button className="bt bt-s" data-testid="piece-supprimer"
              style={{ color: '#b8412b', borderColor: '#e8c4b8' }}
              onClick={onSupprimer}>Supprimer la pièce…</button>
          </div>
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Fermer</button>
          </div>
          <p className="note">Ces réglages s'enregistrent avec la pastille
            « Enregistrer » en haut de la fiche.</p>
        </>
      )} />
  )
}

/* ── « Ce que voit l'agent » — la checklist réelle, aperçu téléphone ── */
function FeuilleVueAgent({ log, pieces, onFermer }) {
  return (
    <Feuille titre="Ce que voit l'agent" testId="feuille-vue-agent" onFermer={onFermer}
      enfants={(
        <>
          <div className="info">Sa checklist, dans l'ordre où vous l'avez rangée.
            Il coche au fur et à mesure.</div>
          <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
            <div style={{ padding: '12px 13px', background: '#3d5166' }}>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#fff',
                margin: '0' }}>{log.nom_complet || log.nom || log.id}</p>
            </div>
            <div style={{ padding: '12px 13px' }}>
              {pieces.length === 0 && (
                <p style={{ fontSize: '11.5px', color: 'var(--gris)', margin: 0 }}>
                  Aucune pièce — il n'a rien à cocher.</p>
              )}
              {pieces.map((p, i) => {
                const items = p.items || []
                const visibles = items.slice(0, 3)
                return (
                  <div key={p.id} style={i > 0
                    ? { borderTop: '1px solid var(--bord)', paddingTop: '11px', marginTop: '11px' }
                    : {}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                      marginBottom: '7px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600',
                        color: 'var(--navy-d)' }}>{p.name}</span>
                      {Number(p.photos_min) > 0 && (
                        <span className="tag t-b">{p.photos_min} photo{p.photos_min > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    {visibles.map((it) => (
                      <div key={it.id} style={{ display: 'flex', alignItems: 'center',
                        gap: '9px', padding: '5px 0' }}>
                        <span style={{ width: '17px', height: '17px', borderRadius: '5px',
                          border: '1.5px solid #c3cdd6', flexShrink: '0' }} />
                        <span style={{ fontSize: '11.5px',
                          color: 'var(--gris)' }}>{it.label}</span>
                      </div>
                    ))}
                    {items.length > 3 && (
                      <p style={{ fontSize: '10px', color: 'var(--gris-d)',
                        margin: '4px 0 0', paddingLeft: '26px' }}>et {items.length - 3} autre{items.length - 3 > 1 ? 's' : ''}</p>
                    )}
                    {!(Number(p.photos_min) > 0) && (
                      <p style={{ fontSize: '10px', color: 'var(--ambre-tx)',
                        margin: '5px 0 0', paddingLeft: '26px' }}>aucune photo
                        demandée sur cette pièce</p>
                    )}
                  </div>
                )
              })}
              <div style={{ marginTop: '14px', background: 'var(--terra)',
                borderRadius: '10px', padding: '11px', textAlign: 'center',
                fontSize: '12px', color: '#fff', fontWeight: '600' }}>Terminer le ménage</div>
            </div>
          </div>
          <p className="note">Il ne peut pas terminer tant qu'une photo exigée manque.</p>
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Fermer</button>
          </div>
        </>
      )} />
  )
}

/* ── la feuille PROVISOIRE des incidents (décision 3) ────────────────
   Catégories, modèles (avec photo de référence ≤ 1 Mo) et photos minimum
   — mécanique de ChecklistsTab conservée, RÉORDONNANCEMENT BRANCHÉ
   (décision « fais-les marcher » : les poignées décoratives deviennent
   des flèches réelles). */
const CATS_DEFAUT = [
  { id: 'casse', label: 'Casse', icon: '🔨', color: '#c0392b' },
  { id: 'salissure', label: 'Salissure', icon: '🧴', color: '#2980b9' },
  { id: 'panne', label: 'Panne', icon: '🔧', color: '#d98c1f' },
  { id: 'vol', label: 'Vol / manque', icon: '🛡️', color: '#5b48a2' },
  { id: 'odeur', label: 'Odeur', icon: '💨', color: '#2f9e6e' },
  { id: 'autre', label: 'Autre', icon: '❓', color: '#777' },
]

function FeuilleIncidentsProvisoire({ incidents, onEcrire, onFermer }) {
  const categories = Array.isArray(incidents.categories) && incidents.categories.length
    ? incidents.categories : CATS_DEFAUT
  const templates = Array.isArray(incidents.templates) ? incidents.templates : []
  const photosMin = incidents.photos_min_per_incident ?? 2
  const [erreurPhoto, setErreurPhoto] = useState(null)

  const patchTemplate = (id, patch) => {
    onEcrire({ templates: templates.map((t) => (t.id === id ? { ...t, ...patch } : t)) })
  }
  const bouger = (liste, cle) => (idx, delta) => {
    const cible = idx + delta
    if (cible < 0 || cible >= liste.length) return
    const next = [...liste]
    const [x] = next.splice(idx, 1)
    next.splice(cible, 0, x)
    onEcrire({ [cle]: next })
  }
  const bougerTemplate = bouger(templates, 'templates')
  const bougerCategorie = bouger(categories, 'categories')

  const chargerPhoto = (tplId) => async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setErreurPhoto(null)
    try {
      if (!file.type.startsWith('image/')) throw new Error('Le fichier doit être une image')
      if (file.size > 1024 * 1024) throw new Error(`Trop volumineux (${(file.size / 1024).toFixed(0)} KB > 1024 KB max)`)
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(new Error('Lecture fichier échouée'))
        reader.readAsDataURL(file)
      })
      patchTemplate(tplId, { photo_b64: b64 })
    } catch (err) { setErreurPhoto(err.message) }
  }

  return (
    <Feuille titre="Les incidents" suffixe="provisoire" large
      testId="feuille-incidents-reglages" onFermer={onFermer}
      enfants={(
        <>
          <div className="info">Ces réglages deviendront <b>uniques pour tout le
            parc</b> dans Réglages · Incidents quand son lot arrivera. En
            attendant, ils restent propres à ce logement et s'éditent ici —
            et s'enregistrent avec la pastille « Enregistrer ».</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px',
            padding: '9px 0', borderBottom: '1px solid var(--bord)',
            marginBottom: '9px' }}>
            <span style={{ flex: '1', fontSize: '12px', color: 'var(--gris)' }}>
              Photos preuve exigées par incident</span>
            <button aria-label="Diminuer photos exigées par incident"
              onClick={() => onEcrire({ photos_min_per_incident: Math.max(0, photosMin - 1) })}
              style={{ width: '32px', height: '32px', borderRadius: '8px',
                border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                color: 'var(--navy)', cursor: 'pointer' }}>−</button>
            <span style={{ minWidth: '24px', textAlign: 'center', fontSize: '14px',
              fontWeight: '600' }}>{photosMin}</span>
            <button aria-label="Augmenter photos exigées par incident"
              onClick={() => onEcrire({ photos_min_per_incident: Math.min(10, photosMin + 1) })}
              style={{ width: '32px', height: '32px', borderRadius: '8px',
                border: '1px solid #c3d5e4', background: '#fff', fontSize: '16px',
                color: 'var(--navy)', cursor: 'pointer' }}>+</button>
          </div>

          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 6px' }}>
            Les catégories ({categories.length})</p>
          {categories.map((cat, idx) => (
            <div key={cat.id} style={{ display: 'flex', alignItems: 'center',
              gap: '7px', marginBottom: '6px' }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '1px',
                flexShrink: 0 }}>
                <button onClick={() => bougerCategorie(idx, -1)} disabled={idx === 0}
                  aria-label="Monter la catégorie" style={flecheStyle(idx > 0)}>▲</button>
                <button onClick={() => bougerCategorie(idx, 1)}
                  disabled={idx === categories.length - 1}
                  aria-label="Descendre la catégorie"
                  style={flecheStyle(idx < categories.length - 1)}>▼</button>
              </span>
              <span style={{ fontSize: '14px', flexShrink: 0 }}>{cat.icon}</span>
              <input value={cat.label || ''}
                onChange={(e) => onEcrire({ categories: categories.map((x) => (x.id === cat.id ? { ...x, label: e.target.value } : x)) })}
                style={{ ...inpFeuille, minHeight: '34px', flex: 1 }} />
              <button onClick={() => {
                if (!window.confirm('Supprimer cette catégorie ?')) return
                onEcrire({ categories: categories.filter((x) => x.id !== cat.id) })
              }} aria-label="Supprimer la catégorie"
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                  color: '#b8412b', fontSize: '14px', flexShrink: 0 }}>✕</button>
            </div>
          ))}
          <button className="bt bt-s" style={{ marginBottom: '12px' }}
            onClick={() => onEcrire({ categories: [...categories,
              { id: nid('cat_'), label: 'Nouvelle catégorie', icon: '❓', color: '#777' }] })}>
            ＋ Catégorie</button>

          <p style={{ fontSize: '11px', color: 'var(--gris)', margin: '0 0 6px' }}>
            Les modèles d'incident ({templates.length})</p>
          {erreurPhoto && (
            <div className="info" style={{ borderColor: '#e8c4b8',
              background: '#fbeee9', color: '#8a3b28' }}>{erreurPhoto}</div>
          )}
          {templates.map((tpl, idx) => (
            <div key={tpl.id} className="carte" style={{ margin: '0 0 7px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
                <span style={{ display: 'flex', flexDirection: 'column', gap: '1px',
                  flexShrink: 0, paddingTop: '2px' }}>
                  <button onClick={() => bougerTemplate(idx, -1)} disabled={idx === 0}
                    aria-label="Monter le modèle" style={flecheStyle(idx > 0)}>▲</button>
                  <button onClick={() => bougerTemplate(idx, 1)}
                    disabled={idx === templates.length - 1}
                    aria-label="Descendre le modèle"
                    style={flecheStyle(idx < templates.length - 1)}>▼</button>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <input value={tpl.label || ''} placeholder="ex : Vaisselle cassée"
                    onChange={(e) => patchTemplate(tpl.id, { label: e.target.value })}
                    style={{ ...inpFeuille, minHeight: '34px', marginBottom: '6px' }} />
                  <textarea value={tpl.action || ''} rows={2}
                    placeholder="Action recommandée pour le ménage"
                    onChange={(e) => patchTemplate(tpl.id, { action: e.target.value })}
                    style={{ ...inpFeuille, minHeight: '48px', resize: 'vertical',
                      padding: '8px 12px', marginBottom: '6px' }} />
                  <div style={{ display: 'flex', gap: '7px', alignItems: 'center',
                    flexWrap: 'wrap' }}>
                    <select value={tpl.category || ''}
                      onChange={(e) => patchTemplate(tpl.id, { category: e.target.value })}
                      style={{ ...inpFeuille, minHeight: '34px', width: 'auto' }}>
                      <option value="">— Catégorie —</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                    {tpl.photo_b64 ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center',
                        gap: '5px' }}>
                        <img src={tpl.photo_b64} alt="" style={{ width: '50px',
                          height: '50px', objectFit: 'cover', borderRadius: '7px' }} />
                        <label className="bt bt-s" style={{ minHeight: '30px',
                          fontSize: '10.5px', cursor: 'pointer' }}>Changer
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={chargerPhoto(tpl.id)} /></label>
                        <button onClick={() => patchTemplate(tpl.id, { photo_b64: null })}
                          aria-label="Retirer la photo"
                          style={{ background: 'none', border: 'none', cursor: 'pointer',
                            color: '#b8412b', fontSize: '13px' }}>✕</button>
                      </span>
                    ) : (
                      <label className="bt bt-s" style={{ minHeight: '30px',
                        fontSize: '10.5px', cursor: 'pointer' }}>📷 Photo réf.
                        <input type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={chargerPhoto(tpl.id)} /></label>
                    )}
                    <button onClick={() => {
                      if (!window.confirm('Supprimer ce modèle ?')) return
                      onEcrire({ templates: templates.filter((x) => x.id !== tpl.id) })
                    }} aria-label="Supprimer le modèle"
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                        color: '#b8412b', fontSize: '14px', marginLeft: 'auto' }}>🗑</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button className="bt bt-s"
            onClick={() => onEcrire({ templates: [...templates,
              { id: nid('i_'), label: '', category: categories[0]?.id || 'autre', action: '' }] })}>
            ＋ Modèle d'incident</button>
          <p className="note">Photos en base64, 1 Mo maximum par photo.</p>
          <div className="act">
            <button className="bt bt-s" onClick={onFermer}>Fermer</button>
          </div>
        </>
      )} />
  )
}
